import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import apiService from '../services/api';
import { getHcRevenue, getSchoolRevenue } from '../utils/transactionRevenue';
import oliveLogo from '../assets/images/olive-logo.png';
import wildfireLogo from '../assets/images/wildfire-logo.png';
import '../styles/StaffPages.css';

const SuperuserTransactionDetail = () => {
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    const fetchTransaction = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();

        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }

        // Fetch all transactions and find the specific one
        const response = await apiService.getAllTransactionsSuperuser();
        const transactionData = response.transactions || [];

        let foundTransaction;

        // Check if this is an index-based lookup or ID-based lookup
        if (id.startsWith('index-')) {
          const index = parseInt(id.replace('index-', ''));
          foundTransaction = transactionData[index];
        } else {
          // Handle both string and number IDs
          foundTransaction = transactionData.find(t => {
            return String(t.id) === String(id);
          });
        }

        if (foundTransaction) {
          setTransaction(foundTransaction);
        } else {
          console.error('Transaction not found with ID:', id);
          console.error('Available transaction IDs:', transactionData.map(t => t.id));
        }
      } catch (error) {
        console.error('Failed to fetch transaction:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransaction();
  }, [id, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getTransactionSource = (transaction) => {
    if (!transaction) return 'Unknown';
    const ext = transaction.external_txn_id && String(transaction.external_txn_id);
    if (ext && ext.startsWith('stripe_checkout_')) {
      return 'stripe';
    }
    if (ext && ext.startsWith('receipt-')) {
      return 'receipt';
    }
    if (transaction.wildfire_merchant_id || transaction.wildfire_merchant_name ||
        (transaction.raw_descriptor && transaction.raw_descriptor.includes('wildfire'))) {
      return transaction.wildfire_application_id === 554 ? 'extension' : 'in-app';
    }
    if (transaction.olive_merchant_id || transaction.olive_merchant_name ||
        (transaction.raw_descriptor && transaction.raw_descriptor.includes('olive'))) {
      return 'in-person';
    }
    return 'unknown';
  };

  const getSourceDisplay = (source) => {
    switch(source) {
      case 'stripe': return 'Direct (Stripe)';
      case 'extension': return 'Extension (Wildfire)';
      case 'in-app': return 'In-app (Wildfire)';
      case 'in-person': return 'In-person (Olive)';
      case 'receipt': return 'Receipt review';
      default: return 'Unknown';
    }
  };

  const getSourceLogo = (source) => {
    if (source === 'extension' || source === 'in-app') return wildfireLogo;
    if (source === 'in-person') return oliveLogo;
    return null;
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <BackButton text="Back to Transaction Management" to="/superuser/transactions/" />
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#666'
            }}>
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <BackButton text="Back to Transaction Management" to="/superuser/transactions/" />
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#666'
            }}>
              Transaction not found
            </div>
          </div>
        </div>
      </div>
    );
  }

  const source = getTransactionSource(transaction);
  const sourceLogo = getSourceLogo(source);

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Transaction Management" to="/superuser/transactions/" />

          {/* Header with Transaction Status */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '32px'
          }}>
            <div>
              <h1 className="staff-page-header">Transaction Details</h1>
              <p className="staff-page-subheader">
                ID: {transaction.id} • {formatDate(transaction.created_at || transaction.auth_at || transaction.captured_at)}
              </p>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              {sourceLogo && (
                <img
                  src={sourceLogo}
                  alt={getSourceDisplay(source)}
                  style={{
                    height: '24px',
                    objectFit: 'contain'
                  }}
                />
              )}
              <span style={{
                fontFamily: '"Baikal Book"',
                fontSize: '14px',
                padding: '8px 16px',
                borderRadius: '100px',
                background: (transaction.status === 'completed' || transaction.status === 'captured' || transaction.status === 'auth') ? '#D4F3D4' :
                          transaction.status === 'pending' ? '#FFF4CE' :
                          transaction.status === 'failed' ? '#FFE4E4' :
                          'rgba(0, 0, 0, 0.06)',
                fontWeight: '500'
              }}>
                {(transaction.status === 'completed' || transaction.status === 'captured' || transaction.status === 'auth') ? 'Complete' :
                 transaction.status === 'pending' ? 'Pending' :
                 transaction.status === 'failed' ? 'Failed' :
                 (transaction.status || 'Unknown')}
              </span>
            </div>
          </div>

          {/* Main Content Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '24px',
            marginBottom: '24px'
          }}>
            {/* Financial Summary Card */}
            <div style={{
              backgroundColor: '#F8F8F8',
              borderRadius: '16px',
              padding: '24px'
            }}>
              <h3 style={{
                fontFamily: '"Baikal Book"',
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '20px',
                color: '#111827'
              }}>Financial Summary</h3>

              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  fontSize: '14px',
                  color: '#666',
                  marginBottom: '4px'
                }}>Transaction Amount</div>
                <div style={{
                  fontSize: '28px',
                  fontWeight: '600',
                  color: '#111827'
                }}>
                  {formatCurrency(transaction.dollar_amount)}
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                paddingTop: '16px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <div>
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    marginBottom: '4px'
                  }}>Total Commission</div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600'
                  }}>
                    {formatCurrency(transaction.commission_amount)}
                  </div>
                </div>
                <div>
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    marginBottom: '4px'
                  }}>Commission Rate</div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600'
                  }}>
                    {transaction.commission_amount && transaction.dollar_amount ?
                      `${((transaction.commission_amount / transaction.dollar_amount) * 100).toFixed(1)}%` :
                      'N/A'}
                  </div>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                paddingTop: '16px',
                marginTop: '16px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <div>
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    marginBottom: '4px'
                  }}>HomeCrowd Revenue</div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#2E7D32'
                  }}>
                    {formatCurrency(getHcRevenue(transaction))}
                  </div>
                </div>
                <div>
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    marginBottom: '4px'
                  }}>School Revenue</div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1565C0'
                  }}>
                    {formatCurrency(getSchoolRevenue(transaction))}
                  </div>
                </div>
              </div>

              {transaction.school_commission_split && (
                <div style={{
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: '1px solid #e5e7eb',
                  fontSize: '13px',
                  color: '#666'
                }}>
                  Revenue Split: School {(transaction.school_commission_split * 100).toFixed(0)}% / HomeCrowd {((1 - transaction.school_commission_split) * 100).toFixed(0)}%
                </div>
              )}
            </div>

            {/* User & School Information Card */}
            <div style={{
              backgroundColor: '#F8F8F8',
              borderRadius: '16px',
              padding: '24px'
            }}>
              <h3 style={{
                fontFamily: '"Baikal Book"',
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '20px',
                color: '#111827'
              }}>User & School Information</h3>

              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>User</div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  marginBottom: '4px'
                }}>
                  {transaction.user_name || 'Unknown User'}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#666'
                }}>
                  {transaction.user_email || 'No email'}
                </div>
                {transaction.user_id && (
                  <div style={{
                    fontSize: '12px',
                    color: '#999',
                    marginTop: '4px'
                  }}>
                    User ID: {transaction.user_id}
                  </div>
                )}
              </div>

              {transaction.school && (
                <div style={{
                  paddingTop: '20px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    {transaction.school.image && (
                      <img
                        src={transaction.school.image}
                        alt={transaction.school.name}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          objectFit: 'cover'
                        }}
                      />
                    )}
                    <div>
                      <div style={{
                        fontSize: '12px',
                        color: '#666',
                        marginBottom: '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>School</div>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '500'
                      }}>
                        {transaction.school.name}
                      </div>
                      {transaction.school.id && (
                        <div style={{
                          fontSize: '12px',
                          color: '#999',
                          marginTop: '2px'
                        }}>
                          School ID: {transaction.school.id}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Merchant Information Card */}
            <div style={{
              backgroundColor: '#F8F8F8',
              borderRadius: '16px',
              padding: '24px'
            }}>
              <h3 style={{
                fontFamily: '"Baikal Book"',
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '20px',
                color: '#111827'
              }}>Merchant Information</h3>

              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Merchant</div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '500'
                }}>
                  {transaction.merchant_name || 'Unknown Merchant'}
                </div>
              </div>

              {transaction.description && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Description</div>
                  <div style={{
                    fontSize: '14px',
                    color: '#333'
                  }}>
                    {transaction.description}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Source</div>
                <div style={{
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {sourceLogo && (
                    <img
                      src={sourceLogo}
                      alt=""
                      style={{
                        height: '16px',
                        objectFit: 'contain'
                      }}
                    />
                  )}
                  {getSourceDisplay(source)}
                </div>
              </div>

              {(transaction.wildfire_merchant_id || transaction.olive_merchant_id) && (
                <div style={{
                  paddingTop: '16px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  {transaction.wildfire_merchant_id && (
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      Wildfire Merchant ID: {transaction.wildfire_merchant_id}
                    </div>
                  )}
                  {transaction.wildfire_merchant_name && (
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      Wildfire Name: {transaction.wildfire_merchant_name}
                    </div>
                  )}
                  {transaction.olive_merchant_id && (
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      Olive Merchant ID: {transaction.olive_merchant_id}
                    </div>
                  )}
                  {transaction.olive_merchant_name && (
                    <div style={{
                      fontSize: '12px',
                      color: '#666'
                    }}>
                      Olive Name: {transaction.olive_merchant_name}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Technical Details Card */}
            <div style={{
              backgroundColor: '#F8F8F8',
              borderRadius: '16px',
              padding: '24px'
            }}>
              <h3 style={{
                fontFamily: '"Baikal Book"',
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '20px',
                color: '#111827'
              }}>Technical Details</h3>

              <div style={{
                display: 'grid',
                gap: '12px'
              }}>
                {transaction.external_txn_id && (
                  <div>
                    <div style={{
                      fontSize: '11px',
                      color: '#666',
                      marginBottom: '2px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>External Transaction ID</div>
                    <div style={{
                      fontSize: '13px',
                      fontFamily: 'monospace',
                      wordBreak: 'break-all'
                    }}>
                      {transaction.external_txn_id}
                    </div>
                  </div>
                )}

                {transaction.card_id && (
                  <div>
                    <div style={{
                      fontSize: '11px',
                      color: '#666',
                      marginBottom: '2px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>Card ID</div>
                    <div style={{
                      fontSize: '13px',
                      fontFamily: 'monospace'
                    }}>
                      {transaction.card_id}
                    </div>
                  </div>
                )}

                {transaction.wildfire_application_id && (
                  <div>
                    <div style={{
                      fontSize: '11px',
                      color: '#666',
                      marginBottom: '2px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>Wildfire App ID</div>
                    <div style={{
                      fontSize: '13px',
                      fontFamily: 'monospace'
                    }}>
                      {transaction.wildfire_application_id}
                    </div>
                  </div>
                )}

                {transaction.raw_descriptor && (
                  <div>
                    <div style={{
                      fontSize: '11px',
                      color: '#666',
                      marginBottom: '2px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>Raw Descriptor</div>
                    <div style={{
                      fontSize: '13px',
                      fontFamily: 'monospace',
                      wordBreak: 'break-all'
                    }}>
                      {transaction.raw_descriptor}
                    </div>
                  </div>
                )}
              </div>

              {/* Timestamps */}
              <div style={{
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: '11px',
                  color: '#666',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Timestamps</div>

                {transaction.created_at && (
                  <div style={{
                    fontSize: '12px',
                    color: '#333',
                    marginBottom: '6px'
                  }}>
                    <span style={{ color: '#666' }}>Created:</span> {formatDate(transaction.created_at)}
                  </div>
                )}
                {transaction.auth_at && (
                  <div style={{
                    fontSize: '12px',
                    color: '#333',
                    marginBottom: '6px'
                  }}>
                    <span style={{ color: '#666' }}>Authorized:</span> {formatDate(transaction.auth_at)}
                  </div>
                )}
                {transaction.captured_at && (
                  <div style={{
                    fontSize: '12px',
                    color: '#333'
                  }}>
                    <span style={{ color: '#666' }}>Captured:</span> {formatDate(transaction.captured_at)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Raw Data Section - Collapsed by default */}
          <details style={{
            backgroundColor: '#F8F8F8',
            borderRadius: '16px',
            padding: '24px',
            marginTop: '24px'
          }}>
            <summary style={{
              fontFamily: '"Baikal Book"',
              fontSize: '16px',
              fontWeight: '600',
              color: '#111827',
              cursor: 'pointer',
              userSelect: 'none'
            }}>
              Raw Transaction Data
            </summary>
            <pre style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              fontSize: '12px',
              overflow: 'auto',
              maxHeight: '400px',
              fontFamily: 'monospace',
              lineHeight: '1.5'
            }}>
              {JSON.stringify(transaction, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
};

export default SuperuserTransactionDetail;